<template>
  <div class="password-change">
    <a-form layout="vertical" @finish="handleSubmit">
      <a-alert
        v-if="submitError"
        :message="submitError"
        type="error"
        show-icon
        class="password-change__error" />

      <a-form-item
        v-if="requiresCurrentPassword"
        :label="t('account.password.current')"
        :validate-status="currentStatus"
        :help="currentHelp">
        <a-input
          v-model:value="currentPassword"
          :type="inputType"
          autocomplete="current-password"
          :disabled="isBusy"
          @input="markCurrentTouched">
          <template #suffix>
            <a-button type="text" class="password-change__toggle" @click.stop="toggleShowPasswords">
              <font-awesome-icon :icon="showPasswords ? ['far', 'eye-slash'] : ['far', 'eye']" />
            </a-button>
          </template>
        </a-input>
      </a-form-item>

      <a-form-item
        :label="t('account.password.new')"
        :validate-status="newPasswordStatus"
        :help="newPasswordHelp">
        <a-input
          v-model:value="newPassword"
          :type="inputType"
          autocomplete="new-password"
          :disabled="isBusy"
          @input="markNewTouched">
          <template #suffix>
            <a-button type="text" class="password-change__toggle" @click.stop="toggleShowPasswords">
              <font-awesome-icon :icon="showPasswords ? ['far', 'eye-slash'] : ['far', 'eye']" />
            </a-button>
          </template>
        </a-input>
      </a-form-item>

      <div class="password-change__requirements">
        <div class="password-change__requirement" :class="requirementStateClass(passwordLengthOk)">
          <font-awesome-icon fixed-width aria-hidden="true" :icon="requirementIcon(passwordLengthOk)" />
          <span>{{ t("account.password.requirements.minLength") }}</span>
        </div>
        <div class="password-change__requirement" :class="requirementStateClass(passwordHasNumber)">
          <font-awesome-icon fixed-width aria-hidden="true" :icon="requirementIcon(passwordHasNumber)" />
          <span>{{ t("account.password.requirements.number") }}</span>
        </div>
      </div>

      <div class="password-strength" aria-hidden="true">
        <div class="password-strength__bar">
          <div
            class="password-strength__fill"
            :class="strengthClass"
            :style="{ width: strengthPercent + '%' }"></div>
        </div>
      </div>

      <a-form-item
        :label="t('account.password.repeat')"
        :validate-status="confirmStatus"
        :help="confirmHelp">
        <a-input
          v-model:value="confirmPassword"
          :type="inputType"
          autocomplete="new-password"
          :disabled="isBusy"
          @input="markConfirmTouched">
          <template #suffix>
            <a-button type="text" class="password-change__toggle" @click.stop="toggleShowPasswords">
              <font-awesome-icon :icon="showPasswords ? ['far', 'eye-slash'] : ['far', 'eye']" />
            </a-button>
          </template>
        </a-input>
      </a-form-item>

      <div v-if="requiresCurrentPassword" class="password-change__forgot">
        <a-button type="link" :disabled="isBusy" @click="handleForgotPassword">
          {{ t('forgot.your.password') }}
        </a-button>
      </div>

      <div class="password-change__actions">
        <a-button :disabled="isBusy" @click="handleCancel">{{ t('global.cancel') }}</a-button>
        <a-button
          type="primary"
          html-type="submit"
          :loading="submitting"
          :disabled="!canSubmit"
          @click.prevent="handleSubmit">
          {{ submitLabel }}
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script setup>
import { message } from "ant-design-vue"
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword
} from "firebase/auth"
import { computed, ref } from "vue"

import { i18n } from "@/i18n"
import reportError from "@/utils/reportError"

const t = (...args) => i18n.global.t(...args)

const props = defineProps({
  hasPasswordProvider: { type: Boolean, default: false }
})

const emit = defineEmits(["close", "updated"])

const showPasswords = ref(false)
const submitting = ref(false)
const submitError = ref("")
const currentPassword = ref("")
const newPassword = ref("")
const confirmPassword = ref("")
const submitAttempted = ref(false)
const currentTouched = ref(false)
const newTouched = ref(false)
const confirmTouched = ref(false)

const requiresCurrentPassword = computed(() => props.hasPasswordProvider)
const isBusy = computed(() => submitting.value)
const inputType = computed(() => (showPasswords.value ? "text" : "password"))
const submitLabel = computed(() =>
  requiresCurrentPassword.value ? t("account.password.change") : t("account.password.set")
)

const passwordHasNumber = computed(() => /\d/.test(newPassword.value))
const passwordLengthOk = computed(() => newPassword.value.length >= 8)
const passwordValid = computed(() => passwordHasNumber.value && passwordLengthOk.value)
const confirmMatches = computed(
  () => confirmPassword.value.length > 0 && confirmPassword.value === newPassword.value
)

const hasInteracted = computed(() => currentTouched.value || newTouched.value || confirmTouched.value)
const showFormValidation = computed(() => submitAttempted.value || hasInteracted.value)
const showNewValidation = computed(() => submitAttempted.value || newTouched.value)
const showConfirmFeedback = computed(() => showFormValidation.value && !!newPassword.value.trim())

const currentStatus = computed(() => {
  if (!requiresCurrentPassword.value) return ""
  if (!showFormValidation.value) return ""
  return currentPassword.value.trim().length > 0 ? "" : "error"
})

const newPasswordStatus = computed(() => {
  if (!showNewValidation.value) return ""
  return passwordValid.value ? "" : "error"
})

const confirmStatus = computed(() => {
  if (!showConfirmFeedback.value) return ""
  if (!confirmPassword.value.trim()) return "error"
  return confirmMatches.value ? "" : "error"
})

const canSubmit = computed(() => {
  if (isBusy.value) return false
  if (!passwordValid.value || !confirmMatches.value) return false
  if (requiresCurrentPassword.value && !currentPassword.value.trim()) return false
  return true
})

const currentHelp = computed(() => {
  if (!requiresCurrentPassword.value) return ""
  if (!showFormValidation.value) return ""
  return currentPassword.value.trim() ? "" : t("account.password.errors.currentRequired")
})

const newPasswordHelp = computed(() => {
  if (!showNewValidation.value) return ""
  return passwordValid.value ? "" : t("account.password.errors.requirements")
})

const confirmHelp = computed(() => {
  if (!showConfirmFeedback.value) return ""
  if (!confirmPassword.value.trim()) return t("account.password.errors.confirmRequired")
  return confirmMatches.value ? "" : t("account.password.errors.mismatch")
})

const markCurrentTouched = () => {
  currentTouched.value = true
}

const markNewTouched = () => {
  newTouched.value = true
}

const markConfirmTouched = () => {
  confirmTouched.value = true
}

const requirementStateClass = (isMet) => {
  if (isMet) return "is-met"
  if (!showNewValidation.value) return "is-pending"
  return "is-missing"
}

const requirementIcon = (isMet) => {
  if (isMet) return ["fas", "circle-check"]
  if (!showNewValidation.value) return ["fas", "circle"]
  return ["fas", "circle-xmark"]
}

const strengthScore = computed(() => {
  let score = 0
  if (newPassword.value.length >= 8) score += 1
  if (newPassword.value.length >= 12) score += 1
  if (/\d/.test(newPassword.value)) score += 1
  if (/[A-Z]/.test(newPassword.value)) score += 1
  if (/[^A-Za-z0-9]/.test(newPassword.value)) score += 1
  return score
})

const strengthPercent = computed(() => Math.min(100, Math.round((strengthScore.value / 5) * 100)))

const strengthClass = computed(() => {
  if (strengthScore.value >= 4) return "is-strong"
  if (strengthScore.value >= 2) return "is-medium"
  return "is-weak"
})

const resolveUserEmail = (user) => {
  if (!user) return ""
  if (user.email) return user.email
  const providerEmail = user.providerData?.find((provider) => provider?.email)?.email
  return providerEmail || ""
}

const toggleShowPasswords = () => {
  showPasswords.value = !showPasswords.value
}

const handleCancel = () => {
  emit("close")
}

const handleForgotPassword = async () => {
  const auth = getAuth()
  const user = auth.currentUser
  const email = resolveUserEmail(user)
  if (!email) {
    message.error(t("global.errors.generic.body"))
    return
  }

  try {
    const rawLocale = i18n.global.locale
    const resolvedLocale = typeof rawLocale === "string" ? rawLocale : rawLocale?.value
    if (resolvedLocale) {
      auth.languageCode = resolvedLocale
    }
    await sendPasswordResetEmail(auth, email)
    message.success(t("global.ok"))
  } catch (error) {
    reportError(error, { context: "PasswordChangeView.handleForgotPassword" })
    message.error(t("global.errors.generic.body"))
  }
}

const handleSubmit = async () => {
  submitError.value = ""
  submitAttempted.value = true
  if (!canSubmit.value) return

  const auth = getAuth()
  const user = auth.currentUser
  if (!user) {
    message.error(t("global.errors.generic.body"))
    return
  }

  submitting.value = true
  try {
    if (requiresCurrentPassword.value) {
      const email = resolveUserEmail(user)
      if (!email) {
        throw new Error("No email available for reauthentication.")
      }
      const credential = EmailAuthProvider.credential(email, currentPassword.value)
      await reauthenticateWithCredential(user, credential)
    }

    await updatePassword(user, newPassword.value)
    if (user?.reload) {
      await user.reload()
    }
    message.success(t("global.ok"))
    emit("updated")
  } catch (error) {
    reportError(error, { context: "PasswordChangeView.handleSubmit" })
    const code = String(error?.code || "")
    if (code === "auth/requires-recent-login") {
      submitError.value = t("account.password.errors.requiresRecentLogin")
    } else if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
      submitError.value = t("account.password.errors.wrongCurrentPassword")
    } else if (code === "auth/weak-password") {
      submitError.value = t("account.password.errors.weakPassword")
    } else if (String(error?.message || "").trim()) {
      submitError.value = String(error.message)
    } else {
      submitError.value = t("global.errors.generic.body")
    }
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.password-change__error {
  margin-bottom: 12px;
}

.password-change__toggle {
  padding: 0 6px;
}

.password-change__requirements {
  margin: -8px 0 12px;
  display: grid;
  gap: 4px;
  font-size: 12px;
}

.password-change__requirement {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
}

.password-change__requirement.is-met {
  color: var(--Ok);
}

.password-change__requirement.is-missing {
  color: var(--Error);
}

.password-strength {
  margin: -4px 0 12px;
}

.password-strength__bar {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.password-strength__fill {
  height: 100%;
  width: 0;
  transition: width 0.2s ease;
  background: #f97316;
}

.password-strength__fill.is-medium {
  background: #f59e0b;
}

.password-strength__fill.is-strong {
  background: #22c55e;
}

.password-change__forgot {
  margin: -4px 0 6px;
}

.password-change__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
